<template>
    <div :class="styleClasses.wrapper">
        <div :class="styleClasses.labelWrapper">
            <div v-if="borderStyling" class="flex items-center gap-2 mb-2">
                <label :for="inputId" class="text-body-6 font-medium" :class="styleClasses.labelColor">
                    {{ label }}
                    <span v-if="required" class="text-interactive-3 text-body-4 leading-0">*</span>
                </label>
                <div class="ml-1 flex">
                    <div v-for="(item, index) in labelIcons" :key="index" @click="(e) => iconClick(e, item)" class="cursor-pointer">
                        <font-awesome-icon
                            :icon="item.icon"
                            class="mr-3"
                            :class="item.class"
                            :title="item.alt"
                            :alt="item.alt"
                        />
                    </div>
                </div>
            </div>
            <p v-if="optionalText" class="text-body-6 font-medium text-right text-default-3"> {{ optionalText }} </p>
        </div>

        <div :class="[styleClasses.inputWrapper, isDisabled && 'pointer-events-none']" class="relative">
            <input
                :id="inputId"
                :name="inputName"
                :autocomplete="autocomplete"
                :value="modelValue"
                @input="$emit('update:modelValue', $event.target.value)"
                :placeholder="placeholder"
                :aria-label="ariaLabel"
                :type="type"
                :disabled="isDisabled"
                :class="[styleClasses.inputError, styleClasses.input, styleClasses.focus]"
            />
            <slot/>
            <p v-if="borderStyling && hasError" class="text-body-7 font-medium mt-1" :class="styleClasses.errorMessage"> {{ hintText }} </p>
        </div>
    </div>
</template>
<script setup>
import {computed} from '@vue/reactivity';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = defineProps({
    type: {
        type: String,
        default: 'text',
        validator: function (value) {
            return ['text', 'number', 'email', 'password'].indexOf(value) !== -1;
        },
    },
    placeholder: {
        type: String,
    },
    ariaLabel: {
        type: String,
        default: null,
    },
    label: {
        type: String,
    },
    labelPosition: {
        type: String,
        default: 'above',
        validator: function (value) {
            return ['above', 'left'].indexOf(value) !== -1;
        }
    },
    hintText: {
        type: String,
        default: null
    },
    optionalText: {
        type: String,
        default: null
    },
    hasError: {
        type: Boolean,
        default: false
    },
    modelValue: {
        type: [String, Number],
    },
    required: {
        type: Boolean,
        default: false,
    },
    isDisabled: {
        type: Boolean,
        default: false,
    },
    borderStyling: {
        type: Boolean,
        default: true
    },
    colorMode: {
        type: String,
        default: 'light',
    },
    labelIcons: {
        type: Array,
        default: () => []
    },
    inputId: {
        type: String,
        default: null,
    },
    inputName: {
        type: String,
        default: null,
    },
    autocomplete: {
        type: String,
        default: null,
    }
})

const emit = defineEmits(['update:modelValue', 'icon-click'])

const styleClasses = computed(() => ({
    wrapper: {
        'flex flex-row align-center': props.labelPosition === 'left' && props.borderStyling
    },
    inputWrapper: {
        'ml-32 mt-1': props.labelPosition === 'left' && props.borderStyling
    },
    labelWrapper: {
        'flex flex-row justify-between': props.labelPosition === 'above' && props.borderStyling
    },
    label: {
        'flex flex-row align-baseline': props.labelPosition === 'left'
    },
    labelColor: props.colorMode === 'dark' ? 'text-dark-default-10' : 'text-default-1',
    inputError: {
        'border-interactive-3 focus:border-interactive-3 hover:border-interactive-3': props.hasError && !props.isDisabled,
        'focus:border-primary-1 hover:border-primary-1': props.colorMode === 'light'
    },
    input: {
        'block w-full xxs:h-12 lg:h-10 text-body-6 font-normal text-default-1 rounded border focus:outline-none focus:ring-0' : props.borderStyling,
        'block w-full h-12 text-body-6 font-normal text-default-1 border-0' : !props.borderStyling,
        'bg-dark-input-background border-dark-input-background hover:border-dark-input-borderHover' : props.colorMode === 'dark',
        'bg-white border-interactive-9': props.colorMode === 'light',
    },
    focus: {
        'focus:border-primary-1': !props.hasError && !props.isDisabled && props.colorMode !== 'dark',
        'focus:border-white': !props.hasError && !props.isDisabled && props.colorMode === 'dark',
    },
    errorMessage: {
        'text-interactive-3': props.hasError,
        'text-gray-500': !props.hasError
    }
}));

function iconClick(e, icon) {
    emit('icon-click', e, icon.event);
}

</script>
